import React, { useEffect, useRef } from 'react';

import { Loader, ProgressBar } from '@evs/react-components';

import oops from '../assets/icon_oops.png';
import success from '../assets/icon_success.png';
import upload from '../assets/icon_upload.png';
import { XtMotionStatus, XtMotionStatusDetails } from '../utils/Enums';

import styles from './DragAndDropVideoFile.module.scss';

interface MessageProps {
  status: XtMotionStatus;
  statusDetails: XtMotionStatusDetails;
  progress: number;
  objectSize: number;
  icon?: string;
  tryAgain?: () => void;
}
export interface DragAndDropVideoFileProps extends MessageProps {
  fileLoaded: (file: File) => void;
  tryAgain?: () => void;
}

export default function DragAndDropVideoFile(props: DragAndDropVideoFileProps): JSX.Element {
  return (
    <div className={styles.fileContainer} id={'fileContainer'}>
      {props.status === XtMotionStatus.DEFAULT ? (
        <DragAndDropVideoFileDefaultMessage
          fileLoaded={props.fileLoaded}
          status={props.status}
          progress={props.progress}
          objectSize={props.objectSize}
          statusDetails={props.statusDetails}
          icon={upload}
        />
      ) : (
        <span />
      )}
      {props.status === XtMotionStatus.PROGRESS || props.status === XtMotionStatus.ANALYZING ? (
        <DragAndDropVideoFileMessage
          status={props.status}
          progress={props.progress}
          objectSize={props.objectSize}
          statusDetails={props.statusDetails}
          icon={upload}
        />
      ) : (
        <span />
      )}
      {props.status === XtMotionStatus.SUCCESS ? (
        <DragAndDropVideoFileMessage
          status={props.status}
          statusDetails={props.statusDetails}
          progress={props.progress}
          objectSize={props.objectSize}
          icon={success}
          tryAgain={props.tryAgain}
        />
      ) : (
        <span />
      )}
      {props.status === XtMotionStatus.FAILURE ? (
        <DragAndDropVideoFileMessage
          status={props.status}
          progress={props.progress}
          objectSize={props.objectSize}
          statusDetails={props.statusDetails}
          icon={oops}
          tryAgain={props.tryAgain}
        />
      ) : (
        <span />
      )}
    </div>
  );
}

function DragAndDropVideoFileDefaultMessage(props: DragAndDropVideoFileProps): JSX.Element {
  const [file, setFile] = React.useState<File>();
  const imageRef = useRef<any>();

  useEffect(() => {
    if (file) {
      props.fileLoaded(file);
    }
  }, [file]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileObject = event?.target?.files?.[0];
    if (!fileObject) {
      return;
    }
    setFile(fileObject);
  };

  const showOpenFileDialog = () => {
    if (imageRef?.current) {
      imageRef.current.click();
    }
  };

  return (
    <div className={styles.dragAndDropDefault} id={'dragAndDropDefault'} onClick={() => showOpenFileDialog()}>
      <img alt="cloud" src={upload} className={styles.dragAndDropImg} />
      <div className={styles.dragAndDropContent} id={'dragAndDropContent'}>
        <span className={styles.dragAndDropContentTextPart1}>
          Drag & drop your file here or <br />
        </span>
        <span className={styles.dragAndDropContentHighlighted}>
          select a file on your <span className={styles.dragAndDropContentHighlightedComputer}>computer</span>
          <span className={styles.dragAndDropContentHighlightedDevice}>device</span>
        </span>
      </div>
      <input
        ref={imageRef}
        type="file"
        style={{ display: 'none' }}
        accept=".mp4, .mxf, .mov"
        onChange={(e) => handleChange(e)}
      />
      <div className={styles.dragAndDropSecured} id={'dragAndDropSecured'}>
        <span className={`evs-icon evs-icon-lock`} />
        SECURED
      </div>
    </div>
  );
}

function DragAndDropVideoFileMessage(props: MessageProps): JSX.Element {
  const tryAgain = () => {
    if (props.tryAgain) {
      props.tryAgain();
    }
  };

  const calculateProgress = (): number => {
    if (props.objectSize === 0) {
      return 0;
    }

    return Math.round((props.progress / props.objectSize) * 100);
  };

  return (
    <div className={styles.dragAndDrop}>
      <img
        hidden={props.status === XtMotionStatus.PROGRESS || props.status === XtMotionStatus.ANALYZING}
        alt="cloud"
        src={props.icon}
        className={styles.dragAndDropImg}
      />
      <div
        className={styles.dragAndDropLoader}
        hidden={!(props.status === XtMotionStatus.ANALYZING || props.status === XtMotionStatus.PROGRESS)}
      >
        <Loader ldSize="large" ldStyle="primary" className={styles.dragAndDropImg} />
      </div>
      <div className={styles.dragAndDropContent} id={'dragAndDropContent'}>
        <div
          className={styles.dragAndDropContentHighlighted}
          id={'dragAndDropContentMessage'}
          hidden={!(props.status === XtMotionStatus.PROGRESS)}
        >
          {calculateProgress()} %
        </div>
        <span className={styles.dragAndDropContentHighlighted}>
          {props.status}
          <br />
        </span>
        <span className={styles.dragAndDropContentMessage}>{props.statusDetails}</span>
        <div className={styles.actionButton} onClick={(e) => tryAgain()} hidden={!props.tryAgain}>
          <span className={'evs-icon evs-icon-refresh'} /> Try Again
        </div>
      </div>
    </div>
  );
}
